/**
 * StatsCalculator - Computes session and segment statistics from classified data.
 *
 * @typedef {import('../types/index.js').GpsPoint} GpsPoint
 * @typedef {import('../types/index.js').Segment} Segment
 * @typedef {import('../types/index.js').SessionStats} SessionStats
 */

import { calculateDistance } from './GpsProcessor.js';

/**
 * Compute detailed stats for a single segment.
 *
 * @param {Segment} segment
 * @returns {{
 *   distance: number,
 *   elevationGain: number,
 *   elevationLoss: number,
 *   maxSpeed: number,
 *   avgSpeed: number,
 *   duration: number,
 *   startAltitude: number,
 *   endAltitude: number,
 *   verticalDrop: number,
 * }}
 */
export function calculateSegmentStats(segment) {
  const { points } = segment;

  if (!points || points.length === 0) {
    return {
      distance: 0,
      elevationGain: 0,
      elevationLoss: 0,
      maxSpeed: 0,
      avgSpeed: 0,
      duration: 0,
      startAltitude: 0,
      endAltitude: 0,
      verticalDrop: 0,
    };
  }

  let distance = 0;
  let elevationGain = 0;
  let elevationLoss = 0;
  let maxSpeed = 0;
  let speedSum = 0;

  for (let i = 0; i < points.length; i++) {
    const p = points[i];
    const spd = p.speed || 0;
    if (spd > maxSpeed) maxSpeed = spd;
    speedSum += spd;

    if (i > 0) {
      distance += calculateDistance(points[i - 1], p);
      const altDiff = p.altitude - points[i - 1].altitude;
      if (altDiff > 0) elevationGain += altDiff;
      else elevationLoss += Math.abs(altDiff);
    }
  }

  const startAltitude = points[0].altitude;
  const endAltitude = points[points.length - 1].altitude;
  const duration = points[points.length - 1].timestamp - points[0].timestamp;

  return {
    distance,
    elevationGain,
    elevationLoss,
    maxSpeed,
    avgSpeed: points.length > 0 ? speedSum / points.length : 0,
    duration,
    startAltitude,
    endAltitude,
    verticalDrop: startAltitude - endAltitude, // positive when descending
  };
}

/**
 * Compute full session statistics from all segments and the raw point track.
 *
 * @param {Segment[]} segments
 * @param {GpsPoint[]} points - Full processed point array (for overall stats)
 * @returns {SessionStats}
 */
export function calculateSessionStats(segments, points) {
  const downhillSegments = segments.filter((s) => s.type === 'downhill');
  const liftSegments = segments.filter((s) => s.type === 'lift');
  const pauseSegments = segments.filter((s) => s.type === 'pause');

  // ─── Run metrics ───────────────────────────────────────────────────────
  const totalRuns = downhillSegments.length;

  let totalVertical = 0;
  let totalRunDistance = 0;
  let maxSpeed = 0;
  let runSpeedSum = 0;
  let longestRun = 0;
  let biggestVertical = 0;

  for (const run of downhillSegments) {
    const stats = calculateSegmentStats(run);

    totalVertical += stats.elevationLoss; // descent meters
    totalRunDistance += stats.distance;

    if (stats.maxSpeed > maxSpeed) maxSpeed = stats.maxSpeed;
    runSpeedSum += stats.avgSpeed;

    if (stats.distance > longestRun) longestRun = stats.distance;
    if (stats.verticalDrop > biggestVertical) biggestVertical = stats.verticalDrop;
  }

  const avgSpeed = totalRuns > 0 ? runSpeedSum / totalRuns : 0;

  // ─── Total distance (all segments) ─────────────────────────────────────
  let totalDistance = 0;
  for (const seg of segments) {
    totalDistance += seg.distance || 0;
  }

  // ─── Time breakdown ────────────────────────────────────────────────────
  const activeTime = downhillSegments.reduce((sum, s) => sum + (s.duration || 0), 0);
  const liftTime = liftSegments.reduce((sum, s) => sum + (s.duration || 0), 0);
  const pauseTime = pauseSegments.reduce((sum, s) => sum + (s.duration || 0), 0);

  // Total session duration from first to last point
  let totalDuration = 0;
  if (points && points.length >= 2) {
    totalDuration = points[points.length - 1].timestamp - points[0].timestamp;
  } else if (segments.length > 0) {
    const first = segments[0].startTime;
    const last = segments[segments.length - 1].endTime;
    totalDuration = last - first;
  }

  return {
    totalRuns,
    totalDistance,
    totalVertical,
    maxSpeed,
    avgSpeed,
    totalDuration,
    activeTime,
    liftTime,
    pauseTime,
    longestRun,
    biggestVertical,
  };
}

/**
 * Compute lightweight live stats from the current points buffer during recording.
 * This is called frequently so it avoids heavy segment classification.
 *
 * @param {GpsPoint[]} points - All recorded points so far
 * @returns {{
 *   runs: number,
 *   vertical: number,
 *   topSpeed: number,
 *   distance: number,
 *   timeSkiing: number,
 *   timeOnLifts: number,
 * }}
 */
export function calculateLiveStats(points) {
  if (!points || points.length < 2) {
    return { runs: 0, vertical: 0, topSpeed: 0, distance: 0, timeSkiing: 0, timeOnLifts: 0 };
  }

  let totalDistance = 0;
  let totalVertical = 0;
  let topSpeed = 0;
  let runs = 0;
  let timeSkiing = 0;
  let timeOnLifts = 0;

  // Simple heuristic counters: detect runs by altitude direction changes
  let wasDescending = false;

  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1];
    const cur = points[i];

    const dist = calculateDistance(prev, cur);
    totalDistance += dist;

    const spd = cur.speed || 0;
    if (spd > topSpeed) topSpeed = spd;

    const altDiff = cur.altitude - prev.altitude;
    const dt = cur.timestamp - prev.timestamp;

    if (altDiff < 0) {
      // Descending
      totalVertical += Math.abs(altDiff);

      if (spd > 3) {
        timeSkiing += dt;
      }

      if (!wasDescending && spd > 3) {
        runs++;
        wasDescending = true;
      }
    } else {
      // Ascending or flat
      if (altDiff > 0 && spd > 1 && spd < 8) {
        timeOnLifts += dt;
      }
      if (wasDescending && spd < 1) {
        wasDescending = false;
      }
    }
  }

  return {
    runs,
    vertical: totalVertical,
    topSpeed,
    distance: totalDistance,
    timeSkiing,
    timeOnLifts,
  };
}
